import { Ref, RefObject, useEffect, useRef } from 'react';
import { DestinationSettings } from '../../../../destinations/DestinationSettings/DestinationSettings';
import { useCreatePipelineContext } from '../../hooks/useCreatePipelineContext';
import { RawIntegration } from '../../../../shared/interfaces/integration';
import { IntegrationFactory } from '../../../factories/integrationFactory';
import { WizardStepBaseProps } from '../../../../wizard/interfaces/wizard-step-base-props';
import { DestinationFactory } from '../../../../destinations/DestinationList/factories/destinationFactory';
import { useAnalyticsTracker } from '../../../../tracking/hooks/useAnalyticsTracker';
import { CreatePipelineTrackers } from '../../../tracking/create-pipeline-trackers';
import { DestinationAPI } from '../../../../destinations/api/DestinationAPI';
import { EntityFormState } from '../../../../entity-form/interfaces/entity-form';
import { PipelineAPI } from '../../../api/PipelineAPI';
import { RawDestination } from '../../../../shared/interfaces/destination';
import { EntityFormImperativeRef } from '../../../../entity-form/EntityForm';
import { TestConnectionPropsFactory } from '../../../../entity-form/factories/testConnectionPropsFactory';
import { throwTemplateFormError } from '../../../../template-form/utils/templateFormErrorUtil';
import { templateFormConnectionParamsFactory } from '../../../../template-form/factories/templateFormConnectionParamsFactory';
import { UpgradePipelineBanner } from '../../../UpgradePipeline/UpgradePipelineBanner';

export function CreatePipelineDestinationSettings({
  completeStep,
  updateWizardStepFooterProps
}: WizardStepBaseProps) {
  const {
    pipeline,
    destination,
    destinationType,
    updateState,
    standardPipelineInfo,
    isUpgradingPipeline
  } = useCreatePipelineContext();

  const { eventTrack } = useAnalyticsTracker();

  const destinationSettingsRef = useRef<EntityFormImperativeRef>();

  useEffect(() => {
    return () => {
      updateWizardStepFooterProps({});
    };
  }, []);

  const formDataResolver = () => {
    if (destination) {
      return DestinationAPI.getDestinationConfigTemplateWithFormData(destination.id);
    }

    if (destinationType) {
      return DestinationAPI.getDestinationConfigTemplateByType(destinationType.destinationType);
    }

    return throwTemplateFormError('Failed to resolve destination');
  };

  const testConnectionResolver = (values: EntityFormState) => {
    if (destination?.id) {
      return DestinationAPI.getTestConnectionTaskIdForExistingDestination(destination.id);
    }

    if (destinationType) {
      const buildParams = templateFormConnectionParamsFactory(
        values,
        destinationType.destinationType
      );
      return DestinationAPI.getTestConnectionTaskIdForNewDestination(
        destinationType.destinationType,
        buildParams
      );
    }

    return throwTemplateFormError('Failed to resolve destination');
  };

  const formSubmitResolver = async (values: EntityFormState) => {
    if (destination && pipeline) {
      return PipelineAPI.configureDestination(pipeline.id, destination.id);
    }

    if (destinationType && pipeline) {
      const connectionParams = templateFormConnectionParamsFactory(
        values,
        destinationType.destinationType
      );

      return DestinationAPI.createDestination(connectionParams).then(res => {
        return PipelineAPI.configureDestination(pipeline.id, res.data.id);
      });
    }

    return throwTemplateFormError('Failed to resolve destination');
  };

  const defaultDestinationNameResolver = destinationType
    ? () => {
        return DestinationAPI.getDefaultDestinationName(destinationType.destinationType);
      }
    : null;

  const formComplete = (rawPipeline: RawIntegration) => {
    if (destination && pipeline) {
      const _pipeline = IntegrationFactory(rawPipeline);

      eventTrack({
        action:
          CreatePipelineTrackers.PIPELINE_TRACKER_CREATE_PIPELINE_DESTINATION_SETTINGS_CONFIGURED_EXISTING_DESTINATION,
        properties: {
          destinationType: _pipeline.destination?.destinationType.destinationType,
          destinationId: _pipeline.destination?.id,
          pipelineId: _pipeline.id
        }
      });

      updateState({
        pipeline: _pipeline,
        destination: _pipeline.destination,
        destinationType: null
      });
    }

    if (destinationType && pipeline) {
      const _destination = DestinationFactory(rawPipeline.destination as RawDestination);

      eventTrack({
        action:
          CreatePipelineTrackers.PIPELINE_TRACKER_CREATE_PIPELINE_DESTINATION_SETTINGS_CREATED_NEW_DESTINATION,
        properties: {
          pipelineId: pipeline.id,
          destinationType: destination?.destinationType.destinationType,
          destinationId: destination?.id
        }
      });

      updateState({
        pipeline: { ...pipeline, destination: _destination, destinationId: _destination.id },
        destination: _destination,
        destinationType: null
      });
    }

    completeStep();
  };

  const formMode = !!destination && destinationType === null ? 'VIEW' : 'CREATE';

  return (
    <DestinationSettings
      mode={formMode}
      topAdornment={
        isUpgradingPipeline ? (
          <UpgradePipelineBanner
            standardPipelineInfo={standardPipelineInfo}
            isObjectConfigPage={false}
          />
        ) : undefined
      }
      isSBSDocsAllowed
      formDataResolver={formDataResolver}
      formSubmitResolver={formSubmitResolver}
      defaultDestinationNameResolver={defaultDestinationNameResolver}
      onComplete={values => formComplete(values as RawIntegration)}
      formImperativeRef={destinationSettingsRef as unknown as Ref<EntityFormImperativeRef>}
      onStateChange={state => {
        const props = TestConnectionPropsFactory(
          state,
          destinationSettingsRef as RefObject<EntityFormImperativeRef>
        );
        updateWizardStepFooterProps(props);
      }}
      testConnectionResolver={testConnectionResolver}
    />
  );
}
